// Ported from genieos/web/src/components/gamma/Card.tsx @ 84923d036
import { forwardRef } from 'react';
import { cn } from '@/utils/cn';
import type { CardProps, CardVariant } from './types';

const variantStyles: Record<CardVariant, string> = {
  default: 'bg-ems-bg-card rounded-xl border border-ems-border p-6',
  elevated: 'bg-ems-bg-card rounded-2xl border border-ems-border p-6',
  nested: 'bg-ems-bg rounded-xl border border-ems-border p-4',
  interactive: 'bg-ems-bg-card rounded-lg border border-ems-border p-4 hover:border-ems-border-bright transition-colors cursor-pointer',
  // §10.3 Small Item Card, non-interactive — the rung a tab-content panel takes
  // (decided 2026-07-27, #2396). Identical to `interactive` minus the hover and
  // cursor affordances, which are wrong on a panel nothing clicks.
  panel: 'bg-ems-bg-card rounded-lg border border-ems-border p-4',
};

// Map legacy Glass variants to Gamma equivalents
const legacyVariantMap: Record<string, CardVariant> = {
  subtle: 'default',
  moderate: 'default',
  intense: 'elevated',
  frosted: 'elevated',
  modern: 'default',
  elevated: 'elevated',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(({
  variant = 'default',
  className,
  children,
  onClick,
  style,
  'data-testid': testId,
  // Legacy props — accepted but ignored
  elevation: _elevation,
  interactive: _interactive,
  glow: _glow,
  glowColor: _glowColor,
  industrial: _industrial,
  touchOptimized: _touchOptimized,
  highContrast: _highContrast,
  ...props
}, ref) => {
  // Resolve legacy variant names
  const resolvedVariant = legacyVariantMap[variant] || variant as CardVariant;
  const hasClick = !!onClick;

  return (
    <div
      ref={ref}
      className={cn(
        variantStyles[resolvedVariant] || variantStyles.default,
        hasClick && 'hover:border-ems-border-bright transition-colors cursor-pointer',
        className
      )}
      onClick={onClick as React.MouseEventHandler<HTMLDivElement>}
      style={style}
      data-testid={testId}
      {...props}
    >
      {children}
    </div>
  );
});

Card.displayName = 'Card';
